/* eslint-disable import/no-cycle */
import React from "react";
import Close from "@pixel/assets/icons/Close";

import Box from '@pixel/core/Box';
import Button from '@pixel/core/Button';
import Drawer from "@pixel/core/Drawer";
import { styled, useTheme, PixelTheme } from "@pixel/core/styles";
import IconButton from "@pixel/core/IconButton";
import Typography from "@pixel/core/Typography";
import Divider from "@pixel/core/Divider";
import ErrorBoundary from "@pixel/core/ErrorBoundary";
import ActionGroup, { ActionGroupProps } from "@pixel/core/ActionGroup";
import ListPaginator, { ListPaginatorOnChange, ListPaginatorProps } from '@pixel/core/ListPaginator';
import ToolTip from "@pixel/core/Tooltip";

type Anchor = "top" | "left" | "bottom" | "right";

export type TDrawerFormCallback = (event: React.MouseEvent<HTMLElement>) => void;

export interface ActionButtonProps {
  /**
  *  Callback for save form
  */
  actionClick?: TDrawerFormCallback;
  /**
  *  action Label
  */
  actionLabel: string;
  /**
  *  css className
  */
  className: string;
  
};

interface FullWidthModalPaginationProps {
  /**
  *  Anchor defines the sliding position of the drawer
  */
  anchor: Anchor;
  /**
  *  Sets the open and closed value of drawer
  */
  open: boolean;
  /**
  *  Callback for save form
  */
  onSaveForm?: TDrawerFormCallback;
  /**
  *  Callback for cancel/close form
  */
  onCancel?: TDrawerFormCallback;
  /**
  *  renders child node
  */
  children?: Node | any;
  /**
  *  drawerWidth can be in % or px
  */
  drawerWidth?: string;
  /**
  *  Drawer Form label
  */
  labelDrawerForm: string;
  /**
  *  Save drawer form button label
  */
  labelSaveDrawerForm: string;
  /**
  *  close button label
  */
  labelClose: string;
  /**
  *  Actions object with links and properties
  */
  SelectedActionGroupProps?: ActionGroupProps;
  /**
  *  button object
  */
  DrawerActionButtonProps?: ActionButtonProps;
  /**
  *  ListPaginatorResults results
  */
  ListPaginatorResults?: any;
  /**
  *  ListPaginatorProps properties
  */
  ListPaginatorProps?: Omit<ListPaginatorProps, 'ListPaginatorOnChange'>;
  /**
  *  onListPaginatorChange event
  */
  onListPaginatorChange?: ListPaginatorOnChange;
}

export const DrawerHeader = styled("div")(
  ({ theme }: { theme: PixelTheme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(3, 3),
    ...theme.mixins.toolbar,
  })
);

export const DrawerFooter = styled("div")(
  ({ theme }: { theme: PixelTheme }) => ({
    bottom: theme.spacing(0),
    "& > div": {
      display: "flex",
      alignItems: "center",
      justifyContent: "right",
      padding: theme.spacing(3, 3),
    },
  })
);

const DrawerStyles = {
  "& .MuiDrawer-paper": {
    height: "100%",
  },
  "@media (max-width: 768px)": {
    "& .MuiDrawer-paper": {
      width: '100%',
    },
  },
  "& ::-webkit-scrollbar": {
    width: "0px",
    background: "transparent"
  },
  'h2': {
    borderBottom: "0",
  },
  ".drawer-footer-actions": {
    ".add-access-button": {
      float: 'left',
      marginLeft: 2,
    }
  }
};

const RightActionSection = {
  minWidth: '240px',
  float: 'right',

  '.paginatorStyle': {
    float: "left",
    position: "relative",
    display: "block",
    borderRight: "1px solid #e4eaee",

    "& > div": {
      display: 'block',
      paddingRight: '0px',

      "& > span": {
        float: 'left',
        lineHeight: '26px',
        marginRight: '15px',
      }
    }
  },

  '.actions': {
    overflow: 'hidden',
    paddingLeft: '8px',
    maxWidth: '93px',
    float: 'right',
  },

  '.actions-button': {
    float: 'right',

    '.MuiBox-root': {
      float: 'left',
      marginRight: '15px',
    }
  }
};

const DrawerContainerStyles = {
  overflow: 'auto',
  height: '100%',
};

const FullWidthModalPagination: React.FC<FullWidthModalPaginationProps> = ({
  anchor,
  open,
  children,
  onSaveForm,
  onCancel,
  ListPaginatorResults,
  onListPaginatorChange,
  ListPaginatorProps,
  SelectedActionGroupProps,
  DrawerActionButtonProps,
  drawerWidth = '40%',
  labelDrawerForm = "Drawer Forms",
  labelSaveDrawerForm = "Save",
  labelClose = "Cancel"
}) => {
  const theme: PixelTheme = useTheme();
  const DrawerHeaderActionsStyles = {
    float: 'left',
    width: theme.spacing(5.625),
  };

  const handleClose = (e) => {
    onCancel(e);
  };


  const TruncatedTypography = ({label, maxLength}: {label: string, maxLength: number}) => {
    if (label?.length > maxLength) {
      const truncatedLabel = label?.length > maxLength ? `${label?.substring(0, maxLength)}...` : label;
      return (
        <ToolTip title={label}>
          <Typography variant="h2">{truncatedLabel}</Typography>
        </ToolTip>
      );
    } else {
      return <Typography variant="h2">{label}</Typography>
    }
  }

  return (
    <ErrorBoundary>
      <div>
        <React.Fragment key={anchor}>
          <Drawer
            sx={{ ...DrawerStyles, '& .MuiDrawer-paper': { width: drawerWidth } }}
            anchor={anchor}
            open={open}
            onClose={handleClose}
          >
            <DrawerHeader>
              <Box>
                <TruncatedTypography label={labelDrawerForm} maxLength={50} />
              </Box>
              <Box sx={RightActionSection}>
                <Box className='paginatorStyle'>
                  <ListPaginator
                    results={ListPaginatorResults}
                    onChange={onListPaginatorChange}
                    {...ListPaginatorProps}
                  />
                </Box>
                <Box className={`${SelectedActionGroupProps ? "actions" : "actions-button"}`}>
                  {SelectedActionGroupProps && (
                    <Box sx={DrawerHeaderActionsStyles}>
                      <ActionGroup {...SelectedActionGroupProps} />
                    </Box>
                  )}
                  {DrawerActionButtonProps && (
                    <Box sx={DrawerContainerStyles}>
                      <Button className={DrawerActionButtonProps.className} disabled={DrawerActionButtonProps.disabled} variant="outlined" onClick={DrawerActionButtonProps.actionClick}>{DrawerActionButtonProps.actionLabel}</Button>
                  </Box>
                  )}
                  <IconButton aria-label={labelClose} onClick={handleClose}>
                    <Close />
                  </IconButton>
                </Box>
              </Box>
            </DrawerHeader>
            <Divider />
            <Box sx={DrawerContainerStyles}>
              {children}
            </Box>
            <Divider />
            <DrawerFooter>
              <Box className="drawer-footer-actions">
                <Button className="add-access-close-button" variant="outlined" onClick={handleClose}>{labelClose}</Button>
                <Button className="add-access-button" variant="contained" onClick={onSaveForm}>{labelSaveDrawerForm}</Button>
              </Box>
            </DrawerFooter>
          </Drawer>
        </React.Fragment>
      </div>
    </ErrorBoundary>
  );
};

export default FullWidthModalPagination;